import { format, parse } from 'date-fns';
import { router, useLocalSearchParams } from 'expo-router';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useCreateNpoPaymentAccountSteps } from '@/api/hooks/paymentAccountsHooks';
import { NpoPaymentAccountStepsResponse, RiskProfile } from '@/api/models/paymentAccountsModels';
import FormWrapper from '@/components/forms/FormWrapper';
import RiskProfileForm from '@/components/forms/paymentNpoOnboarding/RiskProfileForm';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useNpoMerchantOnboardingState } from '@/contexts/npoMerchantOnboardingContext';
import { RiskProfileData } from '@/lib/validations/NpoPaymentOnboarding';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';

const RiskProfileScreen = () => {
  const { npoItem } = useLocalSearchParams<{
    npoItem: string;
  }>();

  const { state: merchantState, setNpoId, setInitialNpoMerchantData } = useNpoMerchantOnboardingState();
  const merchantStateResponse = safeJsonParse<NpoPaymentAccountStepsResponse>(
    merchantState.initialNpoMerchantData ?? ''
  );

  const { mutateAsync: saveStep, isPending } = useCreateNpoPaymentAccountSteps();

  const { showSuccess } = useEndFlowModal();

  const riskProfileData = merchantStateResponse?.result?.riskProfileData;
  const initialData: RiskProfileData = {
    averageTransactionAmount: Number(riskProfileData?.average_transaction_amount ?? 0),
    highestTicket: Number(riskProfileData?.highest_ticket ?? 0),
    monthlyProcessingVolume: Number(riskProfileData?.monthly_processing_volume ?? 0),
    averageCheckAmount: Number(riskProfileData?.average_check_amount ?? 0),
    maximumCheckAchAmount: Number(riskProfileData?.maximum_check_ach_amount ?? 0),
    averageNumberOfChecksPerMonth: Number(riskProfileData?.average_number_of_checks_per_month ?? 0),
    monthlyCheckVolume: Number(riskProfileData?.monthly_check_volume ?? 0),
    businessStartDate:
      riskProfileData?.business_start_date !== undefined
        ? parse(riskProfileData.business_start_date, 'MM/yyyy', new Date()).toISOString()
        : ''
  };

  const parseRiskProfileData = (data: RiskProfileData): RiskProfile => {
    const parsedRiskProfile = {
      average_transaction_amount: data.averageTransactionAmount.toString(),
      highest_ticket: data.highestTicket.toString(),
      monthly_processing_volume: data.monthlyProcessingVolume.toString(),
      average_check_amount: data.averageCheckAmount.toString(),
      maximum_check_ach_amount: data.maximumCheckAchAmount.toString(),
      average_number_of_checks_per_month: data.averageNumberOfChecksPerMonth.toString(),
      monthly_check_volume: data.monthlyCheckVolume.toString(),
      business_start_date: format(new Date(data.businessStartDate), 'MM/yyyy')
    };

    return parsedRiskProfile;
  };

  return (
    <SafeAreaView style={style.flexOne} edges={['bottom']}>
      <FormWrapper scrollEnable minHeightMinus={180}>
        <RiskProfileForm
          initialData={riskProfileData !== undefined && riskProfileData !== null ? initialData : undefined}
          isLoading={isPending}
          onDone={(data) => {
            const parsedRiskProfile = parseRiskProfileData(data);

            saveStep(
              {
                type: 'both',
                body: {
                  nonProfitOrgId: merchantState.npoId ?? '',
                  risk_profile: parsedRiskProfile
                }
              },
              {
                onSuccess: (data) => {
                  setNpoId(data.nonProfitOrgId);
                  setInitialNpoMerchantData(
                    JSON.stringify({
                      files: [],
                      result: data
                    })
                  );
                  router.navigate({
                    pathname: '/npoPaymentOnboarding/bankAccount',
                    params: {
                      npoItem
                    }
                  });
                }
              }
            );
          }}
          onSaveAndContinue={(data) => {
            const parsedRiskProfile = parseRiskProfileData(data);

            saveStep(
              {
                type: 'both',
                body: {
                  nonProfitOrgId: merchantState.npoId ?? '',
                  risk_profile: parsedRiskProfile
                }
              },
              {
                onSuccess: (data) => {
                  setNpoId(data.nonProfitOrgId);
                  setInitialNpoMerchantData(
                    JSON.stringify({
                      files: [],
                      result: data
                    })
                  );
                  showSuccess({
                    title: i18n.t('profile.saved_continue_title'),
                    message: i18n.t('profile.saved_continue_description'),
                    onButtonPress: () => {
                      router.dismissAll();
                      router.navigate('/(app)/(bottomTabs)/profile/npoProfile/insights');
                    }
                  });
                }
              }
            );
          }}
        />
      </FormWrapper>
    </SafeAreaView>
  );
};

export default RiskProfileScreen;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  }
});
